import { useEffect, useState } from "react";
import { KeyRound, Loader2, LogOut, Mail, MessageSquare, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";
import { TwoFactorSetup } from "./TwoFactorSetup";

// Card 2FA nell'area Account → Sicurezza: stato fattori, attivazione TOTP con QR,
// metodi alternativi SMS/email (se abilitati dall'admin), codici di recupero,
// disattivazione (mai per admin) e logout globale da tutti i dispositivi.
export function TwoFactorCard() {
  const { t } = useLanguage();
  const { user, checkAuth, logout } = useAuth();
  const [config, setConfig] = useState({ sms: false, email: false });
  const [setupOpen, setSetupOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newCodes, setNewCodes] = useState(null);

  const twofa = user?.twofa || {};
  const anyFactor = twofa.totp || twofa.sms || twofa.email;

  useEffect(() => {
    api.get("/2fa/config").then(({ data }) => setConfig(data.data)).catch(() => {});
  }, []);

  const errToast = (err) => {
    const d = err.response?.data?.detail || "";
    const known = {
      "2fa_code_invalid": t("tfa.invalidCode"), "Invalid credentials": t("tfa.wrongPassword"),
      "2fa_method_disabled_globally": t("tfa.methodOff"), "2fa_phone_required": t("tfa.phoneRequired"),
      "2fa_totp_first": t("tfa.totpFirst"), "2fa_last_factor": t("tfa.lastFactor"),
      "2fa_admin_mandatory": t("tfa.adminMandatory"),
    };
    toast.error(known[d] || d || t("misc.error"));
  };

  const call = async (path, payload, okMsg, key) => {
    setBusy(key);
    try {
      const { data } = await api.post(path, payload);
      toast.success(okMsg);
      setPassword("");
      setCode("");
      await checkAuth();
      return data.data;
    } catch (err) {
      errToast(err);
      return null;
    } finally {
      setBusy("");
    }
  };

  const regenerate = async () => {
    const out = await call("/2fa/recovery/regenerate", { password, code }, t("tfa.codesRegenerated"), "regen");
    if (out?.recovery_codes) setNewCodes(out.recovery_codes);
  };

  const toggleMethod = async (method, enable) => {
    await call(`/2fa/method/${method}/${enable ? "enable" : "disable"}`, { password, code },
      enable ? t("tfa.methodEnabled") : t("tfa.methodDisabled"), method);
  };

  const disableAll = async () => {
    await call("/2fa/disable", { password, code }, t("tfa.disabled"), "disable");
  };

  const logoutAll = async () => {
    setBusy("logoutAll");
    try {
      await api.post("/auth/logout-all");
      toast.success(t("tfa.logoutAllDone"));
      await logout();
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy("");
    }
  };

  const inputCls = "w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-base sm:text-sm outline-none focus:border-[#FF6B35] transition-colors";

  return (
    <div data-testid="twofa-card" className="mt-8 rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-6 sm:p-8">
      <div className="flex items-center gap-2 font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--accent-terracotta)] mb-2">
        <ShieldCheck className="w-4 h-4" /> {t("tfa.title")}
      </div>
      <p className="text-sm text-[var(--text-secondary)] mb-5">{t("tfa.sub")}</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <span data-testid="twofa-status-totp" className={`rounded-full px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${twofa.totp ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
          Authenticator {twofa.totp ? `✓ ${t("tfa.on")}` : t("tfa.off")}
        </span>
        <span data-testid="twofa-status-sms" className={`rounded-full px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${twofa.sms ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
          SMS {twofa.sms ? `✓ ${t("tfa.on")}` : t("tfa.off")}
        </span>
        <span data-testid="twofa-status-email" className={`rounded-full px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${twofa.email ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
          Email {twofa.email ? `✓ ${t("tfa.on")}` : t("tfa.off")}
        </span>
      </div>

      {user?.is_admin && (
        <p className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">{t("tfa.adminMandatory")}</p>
      )}

      {setupOpen ? (
        <TwoFactorSetup onDone={() => { setSetupOpen(false); checkAuth(); }} onCancel={() => setSetupOpen(false)} />
      ) : (
        <>
          {!twofa.totp && (
            <button type="button" data-testid="twofa-enable-button" onClick={() => setSetupOpen(true)}
              className="rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-7 py-3 font-mono-t text-[11px] uppercase tracking-[0.2em] hover:bg-[#FF6B35] transition-colors">
              {t("tfa.enable")}
            </button>
          )}

          {anyFactor && twofa.totp && (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="twofa-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("tfa.passwordConfirm")} className={inputCls} />
                <input data-testid="twofa-manage-code" value={code} inputMode="numeric" maxLength={12}
                  onChange={(e) => setCode(e.target.value.replace(/[^\dA-Fa-f]/g, ""))}
                  placeholder={t("tfa.codePlaceholder")} className={inputCls} />
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="twofa-regen-codes" onClick={regenerate} disabled={!!busy || !password || code.length < 6}
                  className="rounded-full border border-[var(--border-strong)] px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-40">
                  <KeyRound className="inline w-3.5 h-3.5 mr-1" /> {t("tfa.regenCodes")}
                </button>
                {config.sms && (
                  <button type="button" data-testid="twofa-toggle-sms" onClick={() => toggleMethod("sms", !twofa.sms)} disabled={!!busy || !password || code.length < 6}
                    className="rounded-full border border-[var(--border-strong)] px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-40">
                    <MessageSquare className="inline w-3.5 h-3.5 mr-1" /> SMS {twofa.sms ? t("tfa.disable") : t("tfa.enable")}
                  </button>
                )}
                {config.email && (
                  <button type="button" data-testid="twofa-toggle-email" onClick={() => toggleMethod("email", !twofa.email)} disabled={!!busy || !password || code.length < 6}
                    className="rounded-full border border-[var(--border-strong)] px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-40">
                    <Mail className="inline w-3.5 h-3.5 mr-1" /> Email {twofa.email ? t("tfa.disable") : t("tfa.enable")}
                  </button>
                )}
                {!user?.is_admin && (
                  <button type="button" data-testid="twofa-disable-all" onClick={disableAll} disabled={!!busy || !password || code.length < 6}
                    className="rounded-full border border-rose-300 text-rose-600 px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-rose-50 transition-colors disabled:opacity-40">
                    {t("tfa.disableAll")}
                  </button>
                )}
              </div>
            </div>
          )}

          {newCodes && (
            <div data-testid="twofa-new-codes" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <p className="text-xs text-amber-900 mb-2">{t("tfa.recoveryWarning")}</p>
              <div className="grid grid-cols-2 gap-2">
                {newCodes.map((c) => <code key={c} className="rounded-lg bg-slate-900 text-emerald-300 text-center py-1.5 font-mono-t text-xs tracking-[0.2em]">{c}</code>)}
              </div>
              <button type="button" data-testid="twofa-new-codes-close" onClick={() => setNewCodes(null)}
                className="mt-3 text-xs font-bold uppercase tracking-widest text-amber-900 hover:underline">{t("tfa.codesSaved")}</button>
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-[var(--border-subtle)]">
            <button type="button" data-testid="logout-all-button" onClick={logoutAll} disabled={busy === "logoutAll"}
              className="rounded-full border border-[var(--border-strong)] px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] hover:border-rose-400 hover:text-rose-600 transition-colors disabled:opacity-40">
              {busy === "logoutAll" ? <Loader2 className="inline w-3.5 h-3.5 animate-spin mr-1" /> : <LogOut className="inline w-3.5 h-3.5 mr-1" />}
              {t("tfa.logoutAll")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
